import type {ImageMediaType} from '@wandb/weave/lib/WbAgent/promptParts';

import type {ImagePart} from '../../../agentChat/store/thread';

// Cap each image at 5 MB of raw (pre-encode) bytes — the size users see for
// their file on disk. Note that The base64 wire payload is ~33% larger, which
// is fine because the BE doesn't actually have limits
export const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
export const MAX_IMAGES_PER_TURN = 10;
export const MAX_TOTAL_IMAGE_BYTES = MAX_IMAGE_BYTES * MAX_IMAGES_PER_TURN;
export const ACCEPTED_IMAGE_MEDIA_TYPES: ImageMediaType[] = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
];

export type ImagePartResult =
  | {ok: true; part: ImagePart}
  | {ok: false; reason: 'unsupported-type' | 'too-large' | 'read-failed'};

export type ImagePartsValidation =
  | {ok: true}
  | {ok: false; reason: 'too-many' | 'total-too-large'};

/**
 * An `ImagePart` plus a stable client-side `id`. The id is only used for list
 * rendering and removal in the composer; it is dropped when building the BE
 * prompt payload.
 */
export interface ComposerImage extends ImagePart {
  id: string;
}

/**
 * A composer attachment whose bytes could not be read. It keeps an object-URL
 * preview so the user can see which image failed (revoked on removal), but
 * carries no base64 payload and is therefore never sent.
 */
export interface FailedComposerImage {
  id: string;
  previewUrl: string;
}

/** Wrap an `ImagePart` with a stable id for composer list management. */
export function toComposerImage(part: ImagePart): ComposerImage {
  return {...part, id: crypto.randomUUID()};
}

/** Drop the composer-only `id`, yielding the store/wire `ImagePart`. */
export function toImagePart({id: _id, ...part}: ComposerImage): ImagePart {
  return part;
}

/**
 * Rebuild a `data:` URL for display. We store only the raw base64 payload
 * (prefix stripped for the wire format), so the prefix is re-added here.
 */
export function imagePartToDataUrl(part: ImagePart): string {
  return `data:${part.mediaType};base64,${part.base64Data}`;
}

export function isAcceptedMediaType(type: string): type is ImageMediaType {
  return (ACCEPTED_IMAGE_MEDIA_TYPES as string[]).includes(type);
}

/** Recover the exact decoded (raw) byte count from a base64 payload. */
function base64DecodedBytes(base64Data: string): number {
  const len = base64Data.length;
  if (len === 0) {
    return 0;
  }
  const padding = base64Data.endsWith('==')
    ? 2
    : base64Data.endsWith('=')
      ? 1
      : 0;
  return (len * 3) / 4 - padding;
}

/** Strip the `data:<mime>;base64,` prefix, we want to send only the raw base64 payload. */
function stripDataUrlPrefix(dataUrl: string): string {
  const commaIndex = dataUrl.indexOf(',');
  return commaIndex === -1 ? dataUrl : dataUrl.slice(commaIndex + 1);
}

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('read failed'));
    reader.onload = () => {
      const {result} = reader;
      if (typeof result !== 'string') {
        reject(new Error('unexpected FileReader result'));
        return;
      }
      resolve(stripDataUrlPrefix(result));
    };
    reader.readAsDataURL(file);
  });
}

/**
 * Convert a user-selected image file into an `ImagePart` for the agent prompt.
 *
 * The bytes are base64-encoded as-is. Callers should surface the failure `reason`.
 */
export async function fileToImagePart(file: File): Promise<ImagePartResult> {
  if (!isAcceptedMediaType(file.type)) {
    return {ok: false, reason: 'unsupported-type'};
  }
  if (file.size > MAX_IMAGE_BYTES) {
    return {ok: false, reason: 'too-large'};
  }
  try {
    const base64Data = await readFileAsBase64(file);
    return {
      ok: true,
      part: {type: 'image', mediaType: file.type, base64Data},
    };
  } catch {
    return {ok: false, reason: 'read-failed'};
  }
}

/**
 * Validate the full set of image parts attached to a turn: enforces the
 * per-turn count and the combined raw (decoded) payload size.
 */
export function validateImageParts(
  parts: readonly ImagePart[]
): ImagePartsValidation {
  if (parts.length > MAX_IMAGES_PER_TURN) {
    return {ok: false, reason: 'too-many'};
  }
  const totalRawBytes = parts.reduce(
    (sum, part) => sum + base64DecodedBytes(part.base64Data),
    0
  );
  if (totalRawBytes > MAX_TOTAL_IMAGE_BYTES) {
    return {ok: false, reason: 'total-too-large'};
  }
  return {ok: true};
}
